import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Phone, Mail, MapPin } from "lucide-react";
import { Separator } from "./ui/separator";
import { useLocalizedPath } from "./Header";

export default function Footer() {
  const { t } = useTranslation();
  const { lang = 'de' } = useParams();
  const { getPath, homePath } = useLocalizedPath();
  const currentYear = new Date().getFullYear();

  const navigationLinks = [
    { name: t('nav.home'), href: homePath },
    { name: t('nav.services'), href: getPath('services') },
    { name: t('nav.aboutMe'), href: getPath('about') },
    { name: t('nav.workProcess'), href: getPath('workProcess') },
    { name: t('nav.contact'), href: getPath('contact') },
  ];

  const legalLinks = [
    { name: t('impressum.title'), href: getPath('impressum') },
    { name: t('privacy.title'), href: getPath('privacy') },
    { name: t('cookiePolicy.title'), href: getPath('cookies') },
    { name: t('disclaimer.title'), href: getPath('disclaimer') },
    { name: t('nav.licenseFreeTasks'), href: getPath('licenseFree') },
  ];

  return (
    <footer className="bg-[#2D3748] text-white" data-testid="footer">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Main footer content */}
        <div className="py-12 md:py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company info */}
          <div className="lg:col-span-2">
            <h3 className="text-xl font-bold font-['Manrope'] mb-4">
              {t('header.companyName')} {t('header.companySubtitle')}
            </h3>
            <p className="text-gray-300 mb-6 max-w-md leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
                <span className="text-gray-300">
                  Uelzen und Umgebung
                </span>
              </div>
              <a
                href="tel:+4915207684235"
                className="flex items-center gap-3 text-gray-300 hover:text-orange-400 transition-colors"
                data-testid="footer-phone-link"
              >
                <Phone className="h-5 w-5 text-orange-400 flex-shrink-0" />
                +49 152 07684235
              </a>
              <a
                href="mailto:info@mtrenovierung.de"
                className="flex items-center gap-3 text-gray-300 hover:text-orange-400 transition-colors"
                data-testid="footer-email-link"
              >
                <Mail className="h-5 w-5 text-orange-400 flex-shrink-0" />
                info@mtrenovierung.de
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="text-lg font-semibold font-['Manrope'] mb-4">{t('footer.navigation')}</h4>
            <ul className="space-y-2">
              {navigationLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-orange-400 transition-colors"
                    data-testid={`footer-nav-link-${index}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal links */}
          <div>
            <h4 className="text-lg font-semibold font-['Manrope'] mb-4">{t('footer.legal')}</h4>
            <ul className="space-y-2">
              {legalLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-orange-400 transition-colors"
                    data-testid={`footer-legal-link-${index}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <Separator className="bg-gray-600" />

        {/* Copyright */}
        <div className="py-6 text-center text-gray-400 text-sm">
          <p>{t('footer.copyright', { year: currentYear })}</p>
        </div>
      </div>
    </footer>
  );
}
